import React, { useEffect, useState } from "react";
import {
  DealQuestionnaireQuestionSettings,
  DealsQuestionnaireData,
  QuestionResponse,
  Responses,
  Score
} from "@/pages/deals/types";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import { normalizeStringValue } from "@/utils/strings";
import {
  useDeals,
  useUpdateDealQuestionnaireMutation
} from "@/pages/deals/hooks";
import YesOrNoQuestion from "@/components/controls/Questions/YesOrNoQuestion";
import SingleSelectQuestion from "@/components/controls/Questions/SingleSelectQuestion";
import CheckboxWithTextQuestion from "@/components/controls/Questions/CheckboxWithTextQuestion";
import MultiSelectQuestion from "@/components/controls/Questions/MultiSelectQuestion";
import { groupBy, isEmpty, isEqual } from "lodash";
import { removeQuestionById } from "@/pages/deals/helpers";
import {
  HALF_WRAPPER_CLASSNAME,
  renderQuestionByType,
  renderSectionTitle
} from "@/utils/questionRendering/questionRenderer";
import { useQuestionnaireResponses } from "@/hooks/useQuestionnaireResponses";

interface Props {
  dealId: string;
  questionnaire: DealsQuestionnaireData;
  handleUpdateQuestionnaireScore: (newValue: Score | null) => void;
  responses?: Responses;
  handleRefetch?: () => Promise<void>;
  disabled?: boolean;
}

const GeneralQuestionnaire: React.FC<Props> = ({
  dealId,
  questionnaire,
  handleUpdateQuestionnaireScore,
  responses,
  handleRefetch,
  disabled = false
}) => {
  const { t, i18n } = useTranslation();
  const updateQuestionnaire = useUpdateDealQuestionnaireMutation();
  const { questions: questionnaireQuestions, id: questionnaireId } =
    questionnaire;

  const [questions, setQuestions] = useState(questionnaireQuestions);

  const hasSections = questions?.some((question) => !isEmpty(question.section));

  const {
    entities: [dealData]
  } = useDeals([dealId || ""]);

  useEffect(() => {
    setQuestions(questionnaireQuestions);
  }, [questionnaireQuestions]);

  const { localResponses, updateResponse, resetResponses } =
    useQuestionnaireResponses(responses);

  const updateQuestionnaireResponse = (
    questionId: string,
    newResponse: QuestionResponse
  ) => {
    const currentResponse = localResponses[questionId];
    const currentValue = normalizeStringValue(currentResponse?.response?.value);
    const nextValue = normalizeStringValue(newResponse.response?.value);
    const valuesMatch = currentValue == nextValue;
    const metadataMatch = isEqual(currentResponse ?? {}, newResponse ?? {});

    if (valuesMatch && metadataMatch) {
      return;
    }

    updateResponse(questionId, newResponse);

    updateQuestionnaire.mutate(
      {
        pathParams: { dealId, questionnaireId },
        data: {
          questions: [
            {
              questionId,
              ...newResponse
            }
          ]
        }
      },
      {
        onSuccess: (data: any) => {
          handleUpdateQuestionnaireScore(data.score);
        },
        onError: resetResponses
      }
    );
  };

  const sortedQuestions = questions
    ? [...questions].sort(
        (a, b) => (a.questionOrder ?? 0) - (b.questionOrder ?? 0)
      )
    : [];

  const renderQuestion = (
    question: DealQuestionnaireQuestionSettings,
    questionClassName?: string
  ): JSX.Element => {
    const questionType = question.responseType?.type || FieldDisplayType.TEXT;

    // Handle special cases that need custom components
    if (questionType === "bool") {
      return (
        <YesOrNoQuestion
          key={question.id}
          question={question}
          questionnaireId={questionnaireId}
          dealId={dealId}
          handleUpdateQuestionnaireScore={handleUpdateQuestionnaireScore}
          localResponses={localResponses}
          updateResponse={updateResponse}
          onResponseError={resetResponses}
          updateQuestionsState={(questionId: string) => {
            setQuestions?.((prevQuestions) =>
              removeQuestionById(prevQuestions, questionId)
            );
          }}
          resetQuestionnaire={handleRefetch}
          questionClassName={questionClassName}
          disabled={disabled}
        />
      );
    }

    if (
      questionType === "select" ||
      questionType === FieldDisplayType.SINGLE_SELECT ||
      questionType === FieldDisplayType.PEOPLE
    ) {
      const rawOptions = question.responseType?.options || [];
      const formattedOptions = rawOptions.map((opt: any) => ({
        id: opt.id,
        displayName:
          opt.option || opt.displayName || opt.label || opt.name || opt.id,
        subtitle: opt.subtitle
      }));

      return (
        <SingleSelectQuestion
          key={question.id}
          question={{
            ...question,
            responseType: {
              ...question.responseType,
              options: formattedOptions
            }
          }}
          questionnaireId={questionnaireId}
          handleUpdateQuestionnaireScore={handleUpdateQuestionnaireScore}
          localResponses={localResponses}
          updateResponse={updateResponse}
          onResponseError={resetResponses}
          dealData={dealData}
          className={HALF_WRAPPER_CLASSNAME}
        />
      );
    }

    if (
      questionType === "multiSelect" ||
      questionType === FieldDisplayType.MULTI_SELECT
    ) {
      return (
        <MultiSelectQuestion
          key={question.id}
          question={question}
          questionnaireId={questionnaireId}
          handleUpdateQuestionnaireScore={handleUpdateQuestionnaireScore}
          localResponses={localResponses}
          updateResponse={updateResponse}
          onResponseError={resetResponses}
          dealData={dealData}
          className={HALF_WRAPPER_CLASSNAME}
        />
      );
    }

    if (questionType === "checkboxWithRequiredText") {
      return (
        <CheckboxWithTextQuestion
          key={question.id}
          question={question}
          questionnaireId={questionnaireId}
          dealId={dealId}
          handleUpdateQuestionnaireScore={handleUpdateQuestionnaireScore}
          localResponses={localResponses}
          updateResponse={updateResponse}
          onResponseError={resetResponses}
        />
      );
    }

    // Use the centralized rendering function for all other types
    return renderQuestionByType({
      t,
      question,
      localResponses,
      updateQuestionnaireResponse,
      dealId,
      questionnaireId
    });
  };

  if (hasSections && questions) {
    const groupedQuestions = groupBy(sortedQuestions, "section");
    return (
      <div className="pl-6 flex flex-col gap-6">
        {Object.entries(groupedQuestions).map(([section, sectionQuestions]) => (
          <div key={section} className="flex flex-col gap-3">
            <Text variant="labelSecondary">
              {renderSectionTitle(i18n, section, t)}
            </Text>
            <div>
              {sectionQuestions.map((question) => renderQuestion(question))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col gap-8">
        {sortedQuestions.map((question) => renderQuestion(question))}
      </div>
    </div>
  );
};

export default GeneralQuestionnaire;
